<script setup lang="ts">
import { onMounted, ref, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTenantUsersStore } from '@/@vxapp/TenantUsers/useTenantUsersStore'
import type { TenantUser, TenantUserWritable } from '@/@vxapp/TenantUsers/useTenantUsersStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import type { AbilityAction } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import type { AbilitySubject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import { useAccessContext, inaccessible } from '@/access/useAccessContext'
import TenantUserForm from '@/components/Forms/TenantUsers/TenantUserForm.vue'

definePage({ meta: PageMeta.TenantUsers.List.Meta })
setPageTitle('Tenant users')

const store = useTenantUsersStore()
const route = useRoute()
const router = useRouter()
const rows = shallowRef<TenantUser[]>([])
const selected = ref<Partial<TenantUser> | null>(null)
const dialog = ref<'create' | 'edit' | 'delete' | null>(null)
const error = ref('')
const committedGUID = ref('')
const committedDeleted = ref(false)
const errors = ref<unknown>()
const success = ref('')
const loading = ref(false)
const denied = ref(false)
const search = ref('')
const page = ref(1)
const totalPages = ref(1)
let request = 0
let selectionRequest = 0

const context = useAccessContext(
  () => {
    request += 1
    selectionRequest += 1
    rows.value = []
    selected.value = null
    dialog.value = null
    error.value = ''
    errors.value = undefined
    success.value = ''
    committedGUID.value = ''
    committedDeleted.value = false
    loading.value = false
    denied.value = false
    search.value = ''
    page.value = 1
    totalPages.value = 1
  },
  undefined,
  load
)

function allowed(action: AbilityAction, subject: AbilitySubject) {
  return canPageMeta({ action, subject })
}
async function load() {
  const epoch = context.epoch()
  const version = ++request

  loading.value = true
  error.value = ''
  denied.value = false

  const result = await store.fetchAll({
    page: page.value,
    limit: 25,
    search: search.value || undefined,
  })

  if (!context.isCurrent(epoch) || version !== request) return
  loading.value = false
  if (result.responseCode !== 200 || !('tenantUsers' in result)) {
    rows.value = []
    denied.value = inaccessible(result.responseCode)
    error.value = result.message || 'Tenant users could not be loaded.'

    return
  }
  rows.value = result.tenantUsers
  totalPages.value = Number(store.paginationMeta?.totalPages ?? 1)
}
async function syncQuery() {
  await router.replace({
    query: {
      ...(page.value > 1 ? { page: String(page.value) } : {}),
      ...(search.value ? { search: search.value } : {}),
    },
  })
}
function restoreQuery() {
  const incoming = Number(route.query.page)

  page.value = Number.isInteger(incoming) && incoming > 0 ? incoming : 1
  search.value = typeof route.query.search === 'string' ? route.query.search : ''
}
async function searchRows() {
  page.value = 1
  await syncQuery()
  await load()
}
async function previous() {
  page.value -= 1
  await syncQuery()
  await load()
}
async function next() {
  page.value += 1
  await syncQuery()
  await load()
}
function cancel() {
  selectionRequest += 1
  dialog.value = null
  selected.value = null
  errors.value = undefined
  error.value = ''
}
async function create() {
  if (!allowed(A.CREATE, Subject.TENANT_USERS)) return
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetchTemplate()
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200) {
    denied.value = inaccessible(result.responseCode)
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(store.template ?? {})
  dialog.value = 'create'
}
async function select(row: TenantUser, mode: 'edit' | 'delete') {
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetch(row.userGUID)
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200 || !('tenantUser' in result)) {
    denied.value = inaccessible(result.responseCode)
    if (denied.value) rows.value = []
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(result.tenantUser)
  dialog.value = mode
}
function edit(row: TenantUser) {
  if (allowed(A.UPDATE, Subject.TENANT_USERS_UPDATE_GUID)) void select(row, 'edit')
}
function remove(row: TenantUser) {
  if (allowed(A.DELETE, Subject.TENANT_USERS_DELETE_GUID)) void select(row, 'delete')
}
function view(row: TenantUser) {
  void router.push({ name: PageMeta.TenantUsers.Preview.Name, params: { guid: row.userGUID } })
}
async function save(payload: TenantUserWritable) {
  if (loading.value) return
  const mode = dialog.value
  const identifier = selected.value?.userGUID
  if (mode !== 'create' && (mode !== 'edit' || !identifier)) return
  if (
    !allowed(
      mode === 'create' ? A.CREATE : A.UPDATE,
      mode === 'create' ? Subject.TENANT_USERS : Subject.TENANT_USERS_UPDATE_GUID
    )
  )
    return
  const epoch = context.epoch()
  const actor = context.actor()

  loading.value = true

  const result =
    mode === 'create' ? await store.create(payload) : await store.update(identifier!, payload)

  if (!context.isCurrent(epoch)) {
    context.retainCommittedNotice(result, identifier, actor)

    return
  }
  loading.value = false
  if (result.serviceData?.mutationCommitted) {
    request += 1
    selectionRequest += 1
    rows.value = []
    selected.value = null
    dialog.value = null
    success.value = ''
    errors.value = undefined
    committedGUID.value =
      result.serviceData.userGUID ?? result.serviceData.deletedTenantUserGUID ?? ''
    committedDeleted.value = Boolean(result.serviceData.deletedTenantUserGUID)
    error.value = result.message || 'Account change saved, but login discovery is unavailable.'

    return
  }
  if (result.responseCode !== (mode === 'create' ? 201 : 200)) {
    error.value = result.message || 'The change could not be saved.'
    errors.value = result.errors
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Saved successfully.'
  await load()
}
async function confirmDelete() {
  const identifier = selected.value?.userGUID
  if (!identifier || loading.value || !allowed(A.DELETE, Subject.TENANT_USERS_DELETE_GUID)) return
  const epoch = context.epoch()
  const actor = context.actor()

  loading.value = true

  const result = await store.delete(identifier)
  if (!context.isCurrent(epoch)) {
    context.retainCommittedNotice(result, identifier, actor)

    return
  }
  loading.value = false
  if (result.serviceData?.mutationCommitted) {
    request += 1
    selectionRequest += 1
    rows.value = []
    selected.value = null
    dialog.value = null
    success.value = ''
    errors.value = undefined
    committedGUID.value =
      result.serviceData.userGUID ?? result.serviceData.deletedTenantUserGUID ?? ''
    committedDeleted.value = Boolean(result.serviceData.deletedTenantUserGUID)
    error.value = result.message || 'Account change saved, but login discovery is unavailable.'

    return
  }
  if (result.responseCode !== 200) {
    error.value = result.message || 'Deletion failed.'
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Deleted successfully.'
  await load()
}
function openCommitted() {
  if (committedGUID.value && allowed(A.READ, Subject.TENANT_USERS_GUID))
    void router.push({
      name: PageMeta.TenantUsers.Preview.Name,
      params: { guid: committedGUID.value },
    })
}
restoreQuery()
onMounted(load)
watch(
  () => route.query,
  () => {
    const previousQuery = `${page.value}:${search.value}`

    restoreQuery()
    if (`${page.value}:${search.value}` !== previousQuery) void load()
  }
)
</script>

<template>
  <main data-testid="tenant-users-workspace">
    <h1 class="mb-4">Tenant users</h1>
    <VAlert
      v-if="denied"
      type="error"
      data-testid="tenant-context-unavailable"
      >This resource is inaccessible in the current context.</VAlert
    >
    <VAlert
      v-else-if="error"
      type="error"
      class="mb-4"
      >{{ error }}</VAlert
    >
    <VBtn
      v-if="!committedDeleted && committedGUID && allowed(A.READ, Subject.TENANT_USERS_GUID)"
      @click="openCommitted"
      >Open saved account</VBtn
    >
    <VAlert
      v-if="success"
      type="success"
      class="mb-4"
      >{{ success }}</VAlert
    >
    <VCard>
      <VCardText>
        <VForm
          class="d-flex flex-wrap gap-2 mb-4"
          role="search"
          @submit.prevent="searchRows"
        >
          <AppTextField
            v-model="search"
            label="Search tenant users"
            :rules="[]"
          />
          <VBtn
            type="submit"
            :disabled="loading"
            >Search</VBtn
          >
          <VBtn
            v-if="allowed(A.CREATE, Subject.TENANT_USERS)"
            :disabled="loading"
            @click="create"
            >Create user</VBtn
          >
        </VForm>
        <VProgressLinear
          v-if="loading"
          indeterminate
          aria-label="Loading tenant users"
        />
        <p v-if="!loading && !error && !denied && rows.length === 0">No tenant users found.</p>
        <VTable
          v-if="rows.length"
          density="comfortable"
        >
          <thead>
            <tr>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.userGUID"
              :data-testid="`tenant-user-row-${row.userGUID}`"
            >
              <td>{{ row.email }}</td>
              <td>
                <div class="d-flex flex-wrap gap-1">
                  <VBtn
                    v-if="allowed(A.READ, Subject.TENANT_USERS_GUID)"
                    size="small"
                    @click="() => view(row)"
                    >View</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.UPDATE, Subject.TENANT_USERS_UPDATE_GUID)"
                    size="small"
                    :disabled="loading"
                    @click="() => edit(row)"
                    >Edit</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.DELETE, Subject.TENANT_USERS_DELETE_GUID)"
                    size="small"
                    color="error"
                    :disabled="loading"
                    @click="() => remove(row)"
                    >Delete</VBtn
                  >
                </div>
              </td>
            </tr>
          </tbody>
        </VTable>
        <div class="d-flex flex-wrap align-center gap-2 mt-4">
          <VBtn
            :disabled="loading || page <= 1"
            @click="previous"
            >Previous</VBtn
          ><span>Page {{ page }} of {{ totalPages }}</span
          ><VBtn
            :disabled="loading || page >= totalPages"
            @click="next"
            >Next</VBtn
          >
        </div>
      </VCardText>
    </VCard>
    <VDialog
      :model-value="dialog !== null"
      max-width="700"
      persistent
    >
      <VCard v-if="selected">
        <VCardTitle>{{
          dialog === 'delete' ? 'Delete user?' : dialog === 'create' ? 'Create user' : 'Edit user'
        }}</VCardTitle>
        <VCardText>
          <VAlert
            v-if="error"
            type="error"
            class="mb-4"
            >{{ error }}</VAlert
          >
          <TenantUserForm
            v-if="dialog !== 'delete'"
            :user="selected"
            :is-edit="dialog === 'edit'"
            :loading="loading"
            :errors="errors"
            @submit="save"
            @cancel="cancel"
          />
          <template v-else
            ><p>Delete this user from the current tenant?</p>
            <VBtn
              color="error"
              :loading="loading"
              @click="confirmDelete"
              >Confirm delete</VBtn
            ><VBtn
              :disabled="loading"
              @click="cancel"
              >Cancel</VBtn
            ></template
          >
        </VCardText>
      </VCard>
    </VDialog>
  </main>
</template>
